import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { List, Typography, Grid, Anchor } from 'components/base';
import { CloudinaryPicture } from 'components/rich';
import useDataContext from 'data/context/data';
import BaseNav from 'components/layout/nav';
import Icon, { ICON_TYPE } from 'components/icon';
import SUPPORT_MENU_ITEMS from '../../specifications/support';
import { NAV_ITEMS } from '../../specifications/lists';

import {
  CategoryPageLink,
  ShopPageLink,
  HomePageLink,
  UserPageLink,
  SupportPageLink,
  ProductDetailLink,
} from '../pageLinks';

const Nav = props => {
  const dataContext = useDataContext();

  const items = useMemo(
    () =>
      NAV_ITEMS.reduce((acc, item) => {
        const category = dataContext.getCategory(item);
        if (category) {
          acc.push({ category });
          return acc;
        }

        const product = dataContext.getProduct(item);
        if (product) {
          acc.push({ product });
          return acc;
        }

        return acc;
      }, []),
    [dataContext],
  );

  return (
    <BaseNav topPosition={props.topPosition} expanded={props.expanded}>
      <List className="navbar-nav">
        <List.Item className="nav-item">
          <HomePageLink>
            <Anchor className="nav-link">HOME</Anchor>
          </HomePageLink>
        </List.Item>

        <ProductsItem items={items} />

        <List.Item className="nav-item">
          <UserPageLink section="creations">
            <Anchor className="nav-link">MY PROJECTS</Anchor>
          </UserPageLink>
        </List.Item>

        <SupportItem />

        <List.Item className="nav-item">
          <Anchor href="/blog" className="nav-link">
            BLOG
          </Anchor>
        </List.Item>
      </List>
    </BaseNav>
  );
};

Nav.propTypes = {
  topPosition: PropTypes.number,
  expanded: PropTypes.bool,
};

const ProductsItem = ({ items }) => {
  const hasChild = items.length >= 1;

  const className = classNames({
    'nav-link': true,
    'nav-link-has-child': hasChild,
  });

  return (
    <BaseNav.Item
      hasChild={hasChild}
      link={
        <ShopPageLink>
          <Anchor className={className}>PRODUCTS</Anchor>
        </ShopPageLink>
      }
    >
      {hasChild && (
        <Grid.Row>
          <Grid.Col xs={12} lg={12}>
            {items.map(item => {
              const { category, product } = item;
              if (category) {
                return (
                  <CategoryPageLink key={category.id} category={category}>
                    <Anchor className="header-product">
                      <CloudinaryPicture imageKey={category.image} type="small" />

                      <div>
                        <Typography.H4>
                          <Typography.Span>
                            {category.id === 'family-history' && (
                              <>
                                <Icon type={ICON_TYPE.ANCESTRY} className="text-ancestry" />{' '}
                              </>
                            )}
                            {category.id === 'familysearch' && (
                              <>
                                <Icon type={ICON_TYPE.FAMILY_SEARCH} className="text-familysearch" />{' '}
                              </>
                            )}
                            {category.name.toUpperCase()}
                          </Typography.Span>
                        </Typography.H4>
                        <Typography.P className="small">{category.shortDescription}</Typography.P>
                      </div>
                    </Anchor>
                  </CategoryPageLink>
                );
              }

              if (product) {
                return (
                  <ProductDetailLink key={product.id} productId={product.id}>
                    <Anchor className="header-product">
                      <CloudinaryPicture
                        prefix="products/"
                        imageKey={product.images[0]}
                        type="small"
                      />
                      <div>
                        <Typography.H4>
                          <Typography.Span>{product.name}</Typography.Span>
                        </Typography.H4>
                        <Typography.P className="small">{product.shortDescription}</Typography.P>
                      </div>
                    </Anchor>
                  </ProductDetailLink>
                );
              }

              return null;
            })}
          </Grid.Col>
        </Grid.Row>
      )}
    </BaseNav.Item>
  );
};

ProductsItem.propTypes = {
  items: PropTypes.array.isRequired,
};

const SupportItem = () => {
  const className = classNames({
    'nav-link': true,
    'nav-link-has-child': true,
  });

  return (
    <BaseNav.Item
      hasChild
      link={
        <SupportPageLink>
          <Anchor className={className}>SUPPORT</Anchor>
        </SupportPageLink>
      }
    >
      <Grid.Row>
        <Grid.Col xs={12} lg={12}>
          {SUPPORT_MENU_ITEMS.map(item => (
            <Anchor
              href={item.url}
              targetBlank={item.targetBlank}
              key={item.id}
              className="header-product"
            >
              <img src={item.image} alt={item.label} className="header-support-img" />
              <div className="mt-1">
                <Typography.H4>
                  <Typography.Span>{item.label.toUpperCase()}</Typography.Span>
                </Typography.H4>
                <Typography.P className="small">{item.description}</Typography.P>
              </div>
            </Anchor>
          ))}
        </Grid.Col>
      </Grid.Row>
    </BaseNav.Item>
  );
};

export default Nav;
